import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { Locale } from "@/types";
import ru from "./locales/ru.json";
import uz from "./locales/uz.json";
import en from "./locales/en.json";
import { loadPersisted, savePersisted } from "@/lib/storage";
import { getTelegramUser } from "@/lib/telegram";
import { setApiLocale } from "@/lib/api";

const DICTS: Record<Locale, unknown> = { ru, uz, en };
const LOCALE_KEY = "kms.locale";

function resolvePath(dict: unknown, path: string): string | undefined {
  const parts = path.split(".");
  let node: unknown = dict;
  for (const part of parts) {
    if (typeof node !== "object" || node === null) return undefined;
    node = (node as Record<string, unknown>)[part];
  }
  return typeof node === "string" ? node : undefined;
}

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/{{\s*(\w+)\s*}}/g, (_, key) => String(vars[key] ?? ""));
}

function detectInitialLocale(): Locale {
  const stored = loadPersisted<Locale>(LOCALE_KEY);
  if (stored && (stored === "ru" || stored === "uz" || stored === "en")) return stored;
  const tgCode = getTelegramUser()?.language_code;
  if (tgCode?.startsWith("uz")) return "uz";
  if (tgCode?.startsWith("en")) return "en";
  return "ru"; // default: Uzbekistan-first audience, Russian is the primary language today
}

interface I18nContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (path: string, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const initial = detectInitialLocale();
    setApiLocale(initial);
    return initial;
  });

  const setLocale = useCallback((l: Locale) => {
    // Content language must switch BEFORE the re-render refetches data.
    setApiLocale(l);
    setLocaleState(l);
    savePersisted(LOCALE_KEY, l);
  }, []);

  const t = useCallback(
    (path: string, vars?: Record<string, string | number>) => {
      const value =
        resolvePath(DICTS[locale], path) ??
        resolvePath(DICTS.ru, path) ??
        path; // last-resort: surface the key so missing strings are visible, not blank
      return interpolate(value, vars);
    },
    [locale],
  );

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
